import { useContext, useEffect } from 'react'

import { ComposerContext } from '../ComposerContext'
import { installFluxToEditorStateMirror } from './installFluxToEditorStateMirror'

/**
 * Mount the Flux→Zustand `editorState` identity mirror (CORE-7187 Phase 1).
 *
 * `editorBridgeReady` flips true only after install (which reconciles the
 * full draft set synchronously) and false first on teardown, so a future
 * reader (CORE-7765) never sees a half-seeded collection. Must run inside
 * `ComposerContext.Provider`.
 *
 * @see documentation/work-notes/2026-08-04-composer-flux-to-zustand-bridge.md
 * @see ~/notes/notes/claude/plans/composer/editor-state-migration.md
 */
export function useEditorStateBridge(): void {
  const store = useContext(ComposerContext)
  if (!store) {
    throw new Error('useEditorStateBridge must be used within ComposerProvider')
  }

  useEffect(() => {
    const mirror = installFluxToEditorStateMirror(store)
    store.getState().setEditorBridgeReady(true)

    return (): void => {
      store.getState().setEditorBridgeReady(false)
      mirror.unsubscribe()
    }
  }, [store])
}
